import React, { FC } from 'react';

import {
  Box,
  Divider,
  Flex,
  Skeleton,
  Stack,
  Text,
  Button,
} from '@chakra-ui/react';

import { scrollbarStyles } from 'theme';
import { Link as RouterLink } from 'react-router-dom';

import { AnimatePresence, motion } from 'framer-motion';

import { ReadyForReportProps } from './ReadyForReport.props';
import { useInventoryCounts } from 'hooks/useInventoryCounts';
import { InventoryCountStatus } from 'types/inventory';
import { convertUTCToLocal } from 'utils/dateUtils';
import { useSites } from 'hooks';
import { Tabs } from 'routes/portal/InventoryCount/InventoryCount.props';
import { selectors } from 'store/slices/washhub';

const MotionFlex = motion(Flex);

const getMotionProps = (index: number) => ({
  initial: {
    opacity: 0,
    scale: 0.5,
  },
  animate: {
    opacity: 1,
    scale: 1,
  },
  transition: {
    delay: 0.03 * index,
    ease: 'easeInOut',
    duration: 0.3,
  },
  exit: {
    opacity: 0,
    scale: 0.5,
  },
});

const ReadyForReportView: FC<ReadyForReportProps> = (props) => {
  const { counts: data, total, isFetching } = useInventoryCounts({
    status: InventoryCountStatus.READY_FOR_REPORT,
  });

  const isEmpty = data.length === 0;

  const washHubId = selectors.useSelectedWashHubId();
  const navigateUrl = `/inventory-count?washHubId=${washHubId}&defaultIndex=${Tabs.READY_FOR_REPORT}`;

  const { data: sites } = useSites(true);
  const counts = data.slice(0, 3);
  return (
    <Flex
      p="10px"
      pl="20px"
      minW="300px"
      h="100%"
      bg="#fff"
      minH="480px"
      maxH="480px"
      overflowY="auto"
      css={scrollbarStyles}
      flex={1}
      gap="20px"
      direction="column"
      borderRadius="10px"
      borderWidth="1px">
      <Flex justifyContent="space-between">
        <Text fontSize="22px" fontWeight="600" color="primary.200">
          Ready for Reports ({total ?? 0})
        </Text>
        <Button
          as={RouterLink}
          to={navigateUrl}
          colorScheme="teal"
          backgroundColor="transparent"
          variant="link">
          View All
        </Button>
      </Flex>

      {isFetching ? (
        <>
          <Skeleton height="150px" />
          <Skeleton height="150px" />
          <Skeleton height="150px" />
        </>
      ) : isEmpty ? (
        <Text ml="10px">No reports for the week</Text>
      ) : (
        <AnimatePresence exitBeforeEnter>
          {counts.map((count) => {
            const site = sites?.find((_site) => _site._id === count?.site);

            return (
              <MotionFlex
                bg="#fff"
                key={count._id}
                boxShadow="0 0 10px 0 rgba(0,0,0,0.1)"
                p="10px"
                pr="20px"
                pb="20px"
                borderRadius="10px"
                direction="column"
                //cursor="pointer"
                {...getMotionProps(0)}>
                <Flex gap="10px" align="center">
                  <Box h="15px" w="15px" borderRadius="15px" bg="#00BF13" />
                  <Text noOfLines={1}>{site?.name || ''}</Text>
                </Flex>
                <Text opacity="0.6" ml="25px" fontSize="14px">
                  Report No : {count.countNumber || '-'}
                </Text>
                <Divider my="5px" borderColor="#ccc" />
                <Stack mt="10px" ml="25px" spacing={0} fontSize="14px">
                  <Text>
                    Date:{' '}
                    {convertUTCToLocal(count?.countDate, true, true) || '-'}
                  </Text>
                </Stack>
              </MotionFlex>
            );
          })}
        </AnimatePresence>
      )}
    </Flex>
  );
};

export default ReadyForReportView;
